import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CourierLayout from '../CourierLayout';
import { API } from '../../../config';

const ProofOfDelivery = () => {
  const { orderId } = useParams();
  const history = useHistory();

  const [activeLoads, setActiveLoads] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(orderId || '');
  const [recipientSignature, setRecipientSignature] = useState('');
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [loadingLoads, setLoadingLoads] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const token =
    localStorage.getItem('courier_token') ||
    localStorage.getItem('token');

  const selectedLoad = useMemo(() => {
    return activeLoads.find((load) => {
      const id = load.order_id || load.id;
      return String(id) === String(selectedOrderId);
    });
  }, [activeLoads, selectedOrderId]);

  const normalizeOrders = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.orders)) return data.orders;
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
  };

  const fetchActiveLoads = async () => {
    try {
      setLoadingLoads(true);

      const response = await fetch(`${API}/api/orders/accepted`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      const list = normalizeOrders(data);

      setActiveLoads(list);

      if (!selectedOrderId && list.length > 0) {
        const firstId = list[0].order_id || list[0].id;
        setSelectedOrderId(firstId);
      }
    } catch (error) {
      console.error('Failed to fetch active loads:', error);
      setActiveLoads([]);
    } finally {
      setLoadingLoads(false);
    }
  };

  useEffect(() => {
    fetchActiveLoads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (orderId) {
      setSelectedOrderId(orderId);
    }
  }, [orderId]);

  const handlePhotoChange = (event) => {
    const files = Array.from(event.target.files || []);
    setPhotos(files);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');

    if (!selectedOrderId) {
      setMessage('Please select a load before submitting confirmation.');
      return;
    }

    if (!recipientSignature.trim()) {
      setMessage('Recipient signature is required.');
      return;
    }

    try {
      setSubmitting(true);

      const formData = new FormData();

      formData.append('orderId', selectedOrderId);
      formData.append('signature', recipientSignature.trim());
      formData.append('deliveryNotes', notes.trim());

      photos.forEach((photo) => {
        formData.append('photos', photo);
      });

      const response = await fetch(`${API}/api/proof-of-delivery`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Unable to submit confirmation.');
      }

      setMessage('Confirmation submitted successfully.');
      setRecipientSignature('');
      setNotes('');
      setPhotos([]);

      setTimeout(() => {
        history.push('/courier/order-management');
      }, 1000);
    } catch (error) {
      console.error('POD submit failed:', error);
      setMessage(error.message || 'Unable to submit confirmation.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CourierLayout>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: 1.25,
            color: '#0f172a',
            mb: 1,
            textAlign: 'center',
          }}
        >
          Proof of Completion
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            textAlign: 'center',
            fontSize: '0.95rem',
            lineHeight: 1.5,
          }}
        >
          Submit confirmation after completing an assigned load.
        </Typography>
      </Box>

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          backgroundColor: '#ffffff',
        }}
      >
        {message && (
          <Alert
            severity={message.includes('successfully') ? 'success' : 'error'}
            sx={{ mb: 2, borderRadius: 2 }}
          >
            {message}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select Load</InputLabel>
                <Select
                  label="Select Load"
                  value={selectedOrderId}
                  onChange={(event) => setSelectedOrderId(event.target.value)}
                  disabled={loadingLoads || submitting}
                >
                  <MenuItem value="">
                    Select Load
                  </MenuItem>

                  {activeLoads.map((load) => {
                    const id = load.order_id || load.id;

                    return (
                      <MenuItem key={id} value={id}>
                        #{id} — {load.from_address || 'Pickup'} → {load.to_address || 'Drop-off'}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            {selectedLoad && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#64748b',
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        Pickup
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          color: '#0f172a',
                          fontWeight: 600,
                          lineHeight: 1.5,
                        }}
                      >
                        {selectedLoad.from_address || 'Not provided'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#64748b',
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        Drop-off
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          color: '#0f172a',
                          fontWeight: 600,
                          lineHeight: 1.5,
                        }}
                      >
                        {selectedLoad.to_address || 'Not provided'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: '#0f172a',
                  fontWeight: 700,
                  mb: 1,
                }}
              >
                Recipient Confirmation
              </Typography>

              <TextField
                label="Recipient Signature"
                placeholder="Enter recipient name or signature confirmation"
                fullWidth
                value={recipientSignature}
                onChange={(event) => setRecipientSignature(event.target.value)}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: '#0f172a',
                  fontWeight: 700,
                  mb: 1,
                }}
              >
                Completion Photos
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: '#f8fafc',
                  border: '1px dashed #cbd5e1',
                }}
              >
                <Button
                  variant="outlined"
                  component="label"
                  disabled={submitting}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2,
                  }}
                >
                  Choose Photos
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={handlePhotoChange}
                  />
                </Button>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#64748b',
                    fontSize: '0.95rem',
                    mt: 1,
                  }}
                >
                  {photos.length > 0
                    ? `${photos.length} file${photos.length === 1 ? '' : 's'} selected`
                    : 'No photos selected'}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: '#0f172a',
                  fontWeight: 700,
                  mb: 1,
                }}
              >
                Notes
              </Typography>

              <TextField
                label="Notes"
                placeholder="Add completion notes or comments"
                fullWidth
                multiline
                minRows={4}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'stretch', sm: 'center' },
                  mt: 1,
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitting}
                  sx={{
                    minHeight: 42,
                    px: 4,
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit Confirmation'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </CourierLayout>
  );
};

export default ProofOfDelivery;